import React, { useState, useMemo } from 'react';
import { Trash, Trophy, Leaderboard } from 'iconoir-react';
import { C, St, FONT_BODY, FONT_MONO } from '../theme';
import { RANGES, METODOLOGIE, _globalSettings } from '../constants';
import { calcAvg1RM, calcVolume, calcAvgRPE, hasInferredRPE, rpeZone, filterWarmupSets, rpe2rir, buildExerciseInsights, progressionComment, metodologiaComment, trendLabel, calcTotalReps, calcLoadProjection } from '../science';
import { findExercise } from '../exerciseDB';
import { normalizeHevyName } from '../parsers';
import { openExHistory } from '../events';
import { CommentCard } from './UI';
import { ExerciseCharts, LoadProjectionChart } from './Charts';

export function ExerciseHistoryModal({name, sessions, onClose}) {
  const [range,setRange]=useState("3m");
  const [openSess,setOpenSess]=useState(null);
  const showIntensita = _globalSettings.intensitaAttiva !== false;
  const dbEx=useMemo(()=>findExercise(name),[name]);
  const canonical=dbEx?.name||name;
  const occ=useMemo(()=>{
    const out=[];
    for(const sess of [...sessions].sort((a,b)=>a.date.localeCompare(b.date))){
      for(const ex of sess.exercises){
        const cn=findExercise(normalizeHevyName(ex.name))?.name||ex.name;
        if(cn===canonical||ex.name.toLowerCase()===name.toLowerCase())
          out.push({date:sess.date,workoutName:sess.workoutName,ex});
      }
    }
    return out;
  },[name,sessions,canonical]);
  const cutoff=useMemo(()=>{
    if(range==="all")return null;
    const d=new Date();
    if(range==="1m")d.setMonth(d.getMonth()-1);
    else if(range==="3m")d.setMonth(d.getMonth()-3);
    else if(range==="6m")d.setMonth(d.getMonth()-6);
    return d.toISOString().split("T")[0];
  },[range]);
  const filteredOcc=useMemo(()=>cutoff?occ.filter(o=>o.date>=cutoff):occ,[occ,cutoff]);
  const cd=useMemo(()=>filteredOcc.map(o=>{
    const working=filterWarmupSets(o.ex.sets);const vs=working.filter(s=>Number(s.reps)>0);
    const weights=working.map(s=>parseFloat(s.weight)).filter(w=>w>0);
    return{
      date:o.date.slice(5),
      rm1:Math.round(calcAvg1RM(o.ex.sets)*10)/10,
      volume:Math.round(calcVolume(o.ex.sets)),
      rpe:calcAvgRPE(o.ex.sets)!==null?Math.round(calcAvgRPE(o.ex.sets)*10)/10:null,
      reps:vs.length?Math.round(vs.reduce((a,s)=>a+Number(s.reps),0)/vs.length*10)/10:null,
      peso:weights.length?Math.round(weights.reduce((a,b)=>a+b,0)/weights.length*10)/10:null,
    };
  }),[filteredOcc]);
  const bestRM=occ.length?Math.max(...occ.map(o=>calcAvg1RM(o.ex.sets))):0;
  const bestW=occ.length?Math.max(...occ.flatMap(o=>o.ex.sets.map(s=>parseFloat(s.weight)||0))):0;
  const bestVol=occ.length?Math.max(...occ.map(o=>calcVolume(o.ex.sets))):0;
  const lastOcc=occ[occ.length-1];
  const nextSugg=lastOcc?progressionComment(lastOcc.ex.sets,lastOcc.ex.type||"compound",null,_globalSettings.repRange,dbEx?.equipment):null;
  const occReversed=useMemo(()=>[...occ].reverse(),[occ]);
  const tRM=trendLabel(cd,"rm1"),tPeso=trendLabel(cd,"peso"),tVol=trendLabel(cd,"volume"),tReps=trendLabel(cd,"reps"),tRpe=trendLabel(cd,"rpe");

  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(15,23,42,0.55)",zIndex:200,display:"flex",alignItems:"flex-end"}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:C.card,borderRadius:"20px 20px 0 0",width:"100%",maxWidth:560,margin:"0 auto",maxHeight:"92vh",overflowY:"auto",padding:"20px 18px 48px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
          <div>
            <div style={{fontWeight:700,fontSize:20,color:C.text,letterSpacing:-0.3}}>{name}</div>
            <div style={{display:"flex",gap:6,marginTop:5,flexWrap:"wrap"}}>
              {dbEx?.primaryMuscle&&<span style={{...St.tag(C.blue,C.blueL),fontSize:10}}>{dbEx.primaryMuscle}</span>}
              {dbEx&&<span style={{...St.tag(dbEx.type==="compound"?C.blue:C.purple,dbEx.type==="compound"?C.blueL:C.purpleL),fontSize:10}}>{dbEx.type==="compound"?"Compound":"Isolation"}</span>}
              <span style={{...St.tag(C.muted,C.fill),fontSize:10}}>{occ.length} sessioni</span>
            </div>
          </div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:15,background:C.fill,border:"none",fontSize:16,cursor:"pointer",color:C.subtle,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600}}>×</button>
        </div>

        {occ.length===0
          ?<div style={{textAlign:"center",padding:"40px 0",color:C.muted,fontSize:13}}>Nessuna sessione trovata per "{name}"</div>
          :(<>
            {/* PR cards */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:16}}>
              {[{l:"Best 1RM",v:`${bestRM.toFixed(1)} kg`},{l:"Best Peso",v:`${bestW} kg`},{l:"Best Vol.",v:String(Math.round(bestVol))}].map(p=>(
                <div key={p.l} style={{background:C.amberL,borderRadius:12,padding:"10px 8px",textAlign:"center"}}>
                  <div style={{fontSize:9,fontWeight:700,color:C.amber,letterSpacing:0.8,textTransform:"uppercase",marginBottom:3,display:"flex",alignItems:"center",justifyContent:"center",gap:3}}><Trophy width={9} height={9}/> {p.l}</div>
                  <div style={{fontWeight:700,fontSize:15,...St.mono,color:C.text}}>{p.v}</div>
                </div>
              ))}
            </div>

            {/* Prossima sessione */}
            {nextSugg&&(
              <div style={{...St.card,background:`${nextSugg.color}10`,marginBottom:12}}>
                <div style={{fontSize:10,fontWeight:700,color:nextSugg.color,letterSpacing:1,textTransform:"uppercase",marginBottom:6,display:"flex",alignItems:"center",gap:4}}><Leaderboard width={10} height={10}/> Prossima sessione</div>
                <div style={{fontWeight:700,fontSize:14,color:C.text,marginBottom:4}}>{nextSugg.title}</div>
                <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>{nextSugg.body}</div>
              </div>
            )}

            {/* Range selector */}
            <div style={{display:"flex",background:C.fill,borderRadius:9,padding:2,marginBottom:12,gap:0}}>
              {RANGES.map(r=>{
                const sel=range===r.id;
                return (
                  <button key={r.id} onClick={()=>setRange(r.id)}
                    style={{flex:1,padding:"6px 0",border:"none",borderRadius:7,
                      background:sel?"#fff":"transparent",color:sel?C.text:C.subtle,
                      fontSize:12,fontWeight:sel?600:400,cursor:"pointer",fontFamily:FONT_BODY,
                      boxShadow:sel?"0 1px 3px rgba(0,0,0,0.12)":"none",transition:"all 0.15s"}}>
                    {r.label}
                  </button>
                );
              })}
            </div>

            {/* Charts */}
            {cd.length<2
              ?<div style={{...St.card,textAlign:"center",padding:"20px",color:C.subtle,fontSize:13,marginBottom:12}}>Troppo pochi dati per il periodo selezionato.</div>
              :<ExerciseCharts cd={cd} tRM={tRM} tPeso={tPeso} tVol={tVol} tReps={tReps} tRpe={tRpe}/>
            }

            {/* Storico sessioni — unica card */}
            <div style={{...St.secTit,marginTop:4,marginBottom:10}}>Storico sessioni</div>
            <div style={{...St.card,padding:0,overflow:"hidden"}}>
              {occReversed.map((o,i)=>{
                const rm=calcAvg1RM(o.ex.sets),rpe=calcAvgRPE(o.ex.sets);
                const z=rpe?rpeZone(rpe):null;
                const isOpen=openSess===i;
                const validSets=filterWarmupSets(o.ex.sets).filter(s=>parseFloat(s.weight)>0||Number(s.reps)>0);
                const prevO=occReversed[i+1];
                const insight=progressionComment(o.ex.sets,o.ex.type||"compound",prevO?.ex.sets||null,_globalSettings.repRange,dbEx?.equipment);
                return(
                  <div key={i}>
                    <div onClick={()=>setOpenSess(isOpen?null:i)}
                      style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 14px",cursor:"pointer"}}>
                      <div>
                        <div style={{fontSize:12,color:C.subtle,marginBottom:3}}>
                          {new Date(o.date).toLocaleDateString("it-IT",{weekday:"short",day:"numeric",month:"short"})}
                          {o.workoutName&&<><span style={{margin:"0 5px",color:C.border}}>·</span><span style={{color:C.muted}}>{o.workoutName}</span></>}
                        </div>
                        <div style={{fontWeight:700,fontSize:15,color:C.text,fontFamily:FONT_MONO}}>
                          {rm.toFixed(1)}<span style={{fontSize:12,color:C.subtle,fontWeight:400}}> kg 1RM</span>
                          <span style={{marginLeft:10,fontWeight:500,color:C.muted,fontSize:13}}>{validSets.length} serie</span>
                        </div>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        {insight&&<span style={{width:8,height:8,borderRadius:4,background:insight.color,display:'inline-block',flexShrink:0}}/>}
                        {showIntensita&&z&&<span style={{...St.tag(z.color,z.bg),fontSize:10}}>RPE {rpe.toFixed(1)}</span>}
                        <span style={{color:C.subtle,fontSize:13,marginLeft:4}}>{isOpen?"⌃":"⌄"}</span>
                      </div>
                    </div>
                    {isOpen&&(<>
                      <div style={{borderTop:`0.5px solid ${C.border}`}}/>
                      <div style={{padding:"8px 14px 4px"}}>
                        {validSets.map((s,j)=>{
                          const sRpe=s.rpe?rpeZone(Number(s.rpe)):null;
                          return(
                            <div key={j} style={{display:"flex",alignItems:"center",gap:10,padding:"5px 0",borderBottom:j<validSets.length-1?`0.5px solid ${C.fill}`:"none"}}>
                              <span style={{fontSize:11,color:C.subtle,width:46,flexShrink:0}}>Serie {j+1}</span>
                              <span style={{fontFamily:FONT_MONO,fontWeight:700,fontSize:14,color:C.text,minWidth:52}}>{s.weight||"—"} kg</span>
                              <span style={{fontFamily:FONT_MONO,fontSize:14,color:C.muted}}>× {s.reps||"—"} rep</span>
                              {showIntensita&&sRpe&&<span style={{...St.tag(sRpe.color,sRpe.bg),fontSize:10,marginLeft:"auto"}}>RPE {s.rpe}</span>}
                            </div>
                          );
                        })}
                        {insight&&_globalSettings.showInsights&&(
                          <div style={{margin:"10px 0 6px"}}>
                            <CommentCard c={insight}/>
                          </div>
                        )}
                      </div>
                    </>)}
                    {i<occReversed.length-1&&<div style={{height:"0.5px",background:"rgba(60,60,67,0.12)",margin:"0 14px"}}/>}
                  </div>
                );
              })}
            </div>
          </>)}
      </div>
    </div>
  );
}

export function ExercisePreview({ex, prevEx, allSessions, showInsights=_globalSettings.showInsights, metodologia, onMetodologiaChange, onDelete, onSave}) {
  const [met, setMet] = useState(metodologia||"auto");
  const [localSets, setLocalSets] = useState(()=>ex.sets.map(s=>({...s})));
  const updLocalSet = (si,f,v) => setLocalSets(prev=>{
    const next=prev.map((s,i)=>{
      if(i!==si) return s;
      const ns={...s,[f]:v};
      if(f==="rpe"&&v) ns.rir=String(rpe2rir(v));
      if(f==="rir"&&v) ns.rpe=String(Math.max(6,Math.min(10,10-parseFloat(v))));
      return ns;
    });
    onSave?.({...ex, sets:next});
    return next;
  });
  const validS=localSets.filter(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0);
  const vol=validS.length?Math.round(calcVolume(localSets)):0;
  const rm1=validS.length?Math.round(calcAvg1RM(localSets)):0;
  const totR=calcTotalReps(localSets);
  const avgRPE=calcAvgRPE(validS);
  const avgRIR=avgRPE!==null?rpe2rir(avgRPE):null;
  const zone=avgRPE?rpeZone(avgRPE):null;
  const showIntensita = _globalSettings.intensitaAttiva !== false;
  const displayName = findExercise(ex.name)?.name || ex.name;
  const prevVol = prevEx ? Math.round(calcVolume(prevEx.sets)) : null;
  const volDelta = prevVol && prevVol > 0 ? ((vol - prevVol) / prevVol * 100) : null;

  const handleMet = val => {
    setMet(val);
    if (onMetodologiaChange) onMetodologiaChange(ex.name, val);
  };

  // Se metodologia != auto, usa il commento metodologico invece di quello generico
  const exDbEntry = findExercise(ex.name);
  const exEquipment = exDbEntry?.equipment || null;
  const metComment = validS.length && met!=="auto" ? metodologiaComment(localSets, ex.type, met, exEquipment) : null;
  const insights = (showInsights&&validS.length&&met==="auto")
    ? buildExerciseInsights(localSets,ex.type,prevEx?.sets,ex.name,allSessions||[],met,_globalSettings.repRange,{showRpe:showIntensita&&(_globalSettings.intensita==='rpe'||_globalSettings.intensita==='entrambi'||!_globalSettings.intensita)})
    : null;
  const progFallback = (showInsights&&validS.length&&met==="auto"&&!insights?.primaryInsight)
    ? progressionComment(localSets,ex.type,prevEx?.sets,_globalSettings.repRange,exEquipment)
    : null;

  const selStyle = {
    ...St.input, fontSize:12, padding:"5px 10px", height:30,
    backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat:"no-repeat", backgroundPosition:"right 8px center", paddingRight:24,
    appearance:"none", WebkitAppearance:"none", cursor:"pointer",
  };

  const metDef = METODOLOGIE[met];

  return (
    <div style={{...St.card,marginBottom:10}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,fontSize:17,color:C.text,cursor:"pointer",fontFamily:FONT_BODY,letterSpacing:-0.3}} onClick={()=>openExHistory(ex.name)}>{displayName}</div>
          {ex.machine&&<div style={{fontSize:11,color:C.subtle,marginTop:2}}>({ex.machine})</div>}
          {ex.primaryMuscle&&<div style={{fontSize:12,color:C.subtle,marginTop:2,fontFamily:FONT_BODY}}>{ex.primaryMuscle.charAt(0).toUpperCase()+ex.primaryMuscle.slice(1)}</div>}
          {ex.note&&(
            <div style={{marginTop:6,padding:"8px 12px",background:C.amberL,borderRadius:10,fontSize:12,color:"#92400E",fontStyle:"italic",lineHeight:1.55,borderLeft:`3px solid ${C.amber}`}}>
              "{ex.note}"
            </div>
          )}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0,marginLeft:8,position:"relative"}}>
          <span style={{padding:"4px 11px",borderRadius:20,background:C.fill,color:C.muted,fontSize:12,fontWeight:500,fontFamily:FONT_BODY}}>{ex.type==="compound"?"Multi":"Isol."}</span>
          {onDelete&&(
            <button onClick={e=>{e.stopPropagation();onDelete();}}
              style={{width:28,height:28,borderRadius:14,background:`${C.red}12`,border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Trash width={14} height={14} color={C.red}/>
            </button>
          )}
        </div>
      </div>

      {/* Metodologia select — visibile solo se ci sono più opzioni disponibili */}
      {(()=>{
        const opts=Object.entries(METODOLOGIE).filter(([k])=>
          _globalSettings.metodologieAttive.auto ? k==="auto" : !!_globalSettings.metodologieAttive[k]
        );
        if(opts.length<=1)return null;
        return(
          <div style={{marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:600,color:C.subtle,letterSpacing:0.8,marginBottom:5,textTransform:"uppercase"}}>Metodologia di carico</div>
            <select value={met} onChange={e=>handleMet(e.target.value)} style={selStyle}>
              {opts.map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
            {metDef && met!=="auto" && (
              <div style={{marginTop:6,padding:"6px 10px",background:C.bg,borderRadius:8,fontSize:11,color:C.muted,lineHeight:1.5}}>
                {metDef.desc}
              </div>
            )}
          </div>
        );
      })()}

      {/* Sets table */}
      {(()=>{
        const cols = showIntensita ? "24px 1fr 1fr 1fr 1fr" : "24px 1fr 1fr";
        const headers = showIntensita ? ["KG","REPS","RPE","RIR"] : ["KG","REPS"];
        const rows = localSets.map((s,i)=>({s,i})).filter(({s})=>parseFloat(s.weight)>0||parseInt(s.reps)>0);
        const cellStyle = {background:C.fill,borderRadius:9,padding:"10px 0",textAlign:"center",fontFamily:FONT_BODY,fontSize:14,fontWeight:600,color:C.text};
        return (
          <>
            <div style={{display:"grid",gridTemplateColumns:cols,gap:8,marginBottom:8}}>
              <div/>{headers.map(h=><div key={h} style={{fontSize:10,fontWeight:600,color:C.subtle,textAlign:"center",letterSpacing:0.4}}>{h}</div>)}
            </div>
            {rows.map(({s,i},di)=>(
              <div key={i} style={{display:"grid",gridTemplateColumns:cols,gap:8,marginBottom:8,alignItems:"center"}}>
                <div style={{fontSize:11,color:C.subtle,textAlign:"center",fontFamily:FONT_BODY}}>{di+1}</div>
                <input type="number" inputMode="decimal" value={s.weight||''} placeholder="—"
                  onChange={e=>updLocalSet(i,'weight',e.target.value)}
                  style={{...cellStyle,border:"none",outline:"none",width:"100%",boxSizing:"border-box"}}/>
                <input type="number" inputMode="decimal" value={s.reps||''} placeholder="—"
                  onChange={e=>updLocalSet(i,'reps',e.target.value)}
                  style={{...cellStyle,border:"none",outline:"none",width:"100%",boxSizing:"border-box"}}/>
                {showIntensita&&<div style={cellStyle}>{s.rpe||"—"}</div>}
                {showIntensita&&<div style={cellStyle}>{s.rpe?rpe2rir(s.rpe).toFixed(1):"—"}</div>}
              </div>
            ))}
          </>
        );
      })()}

      {/* Add/remove serie */}
      <div style={{display:"flex",gap:8,marginTop:4,marginBottom:8}}>
        <button onClick={()=>setLocalSets(prev=>{const next=[...prev,{weight:"",reps:"",rpe:"",rir:""}];onSave?.({...ex,sets:next});return next;})}
          style={{flex:1,padding:"8px",border:`1.5px dashed ${C.border}`,borderRadius:10,background:"none",color:C.muted,fontSize:12,cursor:"pointer",fontFamily:FONT_BODY}}>
          + Serie
        </button>
        {localSets.length>1&&(
          <button onClick={()=>setLocalSets(prev=>{const next=prev.slice(0,-1);onSave?.({...ex,sets:next});return next;})}
            style={{padding:"8px 14px",border:`1.5px dashed ${C.red}50`,borderRadius:10,background:"none",color:C.red,fontSize:12,cursor:"pointer",fontFamily:FONT_BODY}}>
            − Serie
          </button>
        )}
      </div>

      {/* Stats + commento */}
      {validS.length>0&&(<>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",padding:"12px 0 4px"}}>
          <div style={{display:"flex",flexWrap:"wrap",gap:16}}>
            {[{label:"Vol",val:vol,unit:"kg·r"},{label:"1RM",val:rm1,unit:"kg"},{label:"Reps",val:totR,unit:""},
              ...(showIntensita&&avgRPE!==null?[{label: hasInferredRPE(ex.sets) ? "RPE ~" : "RPE", val:avgRPE.toFixed(1), unit:"", inferred: hasInferredRPE(ex.sets)}]:[]),
              ...(showIntensita&&avgRIR!==null?[{label:"RIR",val:avgRIR.toFixed(1),unit:""}]:[])
            ].map(s=>(
              <div key={s.label}>
                <div style={{fontFamily:FONT_BODY,fontWeight:600,fontSize:15,color:s.inferred?C.muted:C.text}}>
                  {s.val}<span style={{fontSize:10,color:C.subtle,marginLeft:2}}>{s.unit}</span>
                </div>
                <div style={{fontSize:10,color:C.subtle,marginTop:2,fontFamily:FONT_BODY,display:"flex",alignItems:"center",gap:2}}>
                  {s.label}{s.inferred&&<span style={{fontSize:9,background:C.fill,borderRadius:4,padding:"1px 4px",color:C.muted}}>auto</span>}
                </div>
              </div>
            ))}
            {showIntensita&&zone&&<div style={{display:"flex",alignItems:"center",gap:4}}>
              <span style={St.tag(zone.color,zone.bg)}>{zone.text}</span>
              {hasInferredRPE(ex.sets)&&<span style={{fontSize:10,color:C.subtle,fontStyle:"italic"}}>stimato</span>}
            </div>}
          </div>
          {volDelta!==null&&(
            <div style={{textAlign:"right",flexShrink:0,marginLeft:8}}>
              <div style={{fontFamily:FONT_BODY,fontWeight:700,fontSize:15,color:volDelta===0?C.subtle:volDelta>0?C.green:C.red}}>
                {volDelta>0?"+":""}{volDelta.toFixed(1)}%
              </div>
              <div style={{fontSize:10,color:C.subtle,marginTop:2,fontFamily:FONT_BODY}}>vs prec.</div>
            </div>
          )}
        </div>
        {showInsights&&metComment && <CommentCard c={[metComment]}/>}
        {showInsights&&(insights?.primaryInsight||progFallback||insights?.secondaryInsights?.length>0)&&(
          <CommentCard c={[
            ...(insights?.primaryInsight?[insights.primaryInsight]:progFallback?[progFallback]:[]),
            ...(insights?.secondaryInsights||[])
          ]}/>
        )}
      </>)}

    </div>
  );
}

export function ExerciseDetailView({name,sessions,onBack}) {
  const [range,setRange]=useState("3m");
  const [openSess,setOpenSess]=useState(null);
  const showIntensita = _globalSettings.intensitaAttiva !== false;
  const dbEx=useMemo(()=>findExercise(name),[name]);
  const canonical=dbEx?.name||name;
  const occ=useMemo(()=>{
    const out=[];
    for(const sess of [...sessions].sort((a,b)=>a.date.localeCompare(b.date))){
      for(const ex of sess.exercises){
        const cn=findExercise(normalizeHevyName(ex.name))?.name||ex.name;
        if(cn===canonical||ex.name.toLowerCase()===name.toLowerCase())
          out.push({date:sess.date,workoutName:sess.workoutName,ex});
      }
    }
    return out;
  },[name,sessions,canonical]);
  const cutoff=useMemo(()=>{
    if(range==="all")return null;
    const d=new Date();
    if(range==="1m")d.setMonth(d.getMonth()-1);
    else if(range==="3m")d.setMonth(d.getMonth()-3);
    else if(range==="6m")d.setMonth(d.getMonth()-6);
    return d.toISOString().split("T")[0];
  },[range]);
  const filteredOcc=useMemo(()=>cutoff?occ.filter(o=>o.date>=cutoff):occ,[occ,cutoff]);
  const cd=useMemo(()=>filteredOcc.map(o=>{
    const working=filterWarmupSets(o.ex.sets);const vs=working.filter(s=>Number(s.reps)>0);
    const weights=working.map(s=>parseFloat(s.weight)).filter(w=>w>0);
    return{
      date:o.date.slice(5),
      rm1:Math.round(calcAvg1RM(o.ex.sets)*10)/10,
      volume:Math.round(calcVolume(o.ex.sets)),
      rpe:calcAvgRPE(o.ex.sets)!==null?Math.round(calcAvgRPE(o.ex.sets)*10)/10:null,
      reps:vs.length?Math.round(vs.reduce((a,s)=>a+Number(s.reps),0)/vs.length*10)/10:null,
      peso:weights.length?Math.round(weights.reduce((a,b)=>a+b,0)/weights.length*10)/10:null,
    };
  }),[filteredOcc]);
  const bestRM=occ.length?Math.max(...occ.map(o=>calcAvg1RM(o.ex.sets))):0;
  const bestW=occ.length?Math.max(...occ.flatMap(o=>o.ex.sets.map(s=>parseFloat(s.weight)||0))):0;
  const bestVol=occ.length?Math.max(...occ.map(o=>calcVolume(o.ex.sets))):0;
  const lastOcc=occ[occ.length-1];
  const nextSugg=lastOcc?progressionComment(lastOcc.ex.sets,lastOcc.ex.type||"compound",null,_globalSettings.repRange,dbEx?.equipment):null;
  const occReversed=useMemo(()=>[...occ].reverse(),[occ]);
  const tRM=trendLabel(cd,"rm1"),tPeso=trendLabel(cd,"peso"),tVol=trendLabel(cd,"volume"),tReps=trendLabel(cd,"reps"),tRpe=trendLabel(cd,"rpe");
  const projection=useMemo(()=>filteredOcc.length>=3?calcLoadProjection(filteredOcc):null,[filteredOcc]);

  // set table columns — same layout as ExercisePreview
  const cols = showIntensita ? "24px 1fr 1fr 1fr 1fr" : "24px 1fr 1fr";
  const headers = showIntensita ? ["KG","REPS","RPE","RIR"] : ["KG","REPS"];
  const cellStyle = {background:C.fill,borderRadius:9,padding:"8px 0",textAlign:"center",fontFamily:FONT_BODY,fontSize:14,fontWeight:600,color:C.text};

  return(
    <div style={St.wrap}>
      <div style={{paddingTop:"max(56px, calc(env(safe-area-inset-top, 0px) + 14px))",paddingRight:"20px",paddingBottom:"8px",paddingLeft:"20px",background:C.bg,display:"flex",alignItems:"center",gap:12}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:C.blue,fontSize:17,cursor:"pointer",fontFamily:FONT_BODY,fontWeight:500,flexShrink:0}}>‹ Esercizi</button>
      </div>
      <div style={{padding:"0 16px 16px"}}>
        <div style={{fontWeight:700,fontSize:26,color:C.text,letterSpacing:-0.5,marginBottom:6}}>{name}</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
          {dbEx?.primaryMuscle&&<span style={{...St.tag(C.blue,C.blueL),fontSize:11}}>{dbEx.primaryMuscle}</span>}
          {dbEx&&<span style={{...St.tag(dbEx.type==="compound"?C.blue:C.purple,dbEx.type==="compound"?C.blueL:C.purpleL),fontSize:11}}>{dbEx.type==="compound"?"Compound":"Isolation"}</span>}
          <span style={{...St.tag(C.muted,C.fill),fontSize:11}}>{occ.length} sessioni</span>
        </div>
        {occ.length===0
          ?<div style={{textAlign:"center",padding:"40px 0",color:C.muted,fontSize:14}}>Nessuna sessione per "{name}".<br/>Inizia ad allenarti o importa la cronologia da Hevy.</div>
          :(<>
            {/* PR cards */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:16}}>
              {[{l:"Best 1RM",v:`${bestRM.toFixed(1)} kg`},{l:"Best Peso",v:`${bestW} kg`},{l:"Best Vol.",v:String(Math.round(bestVol))}].map(p=>(
                <div key={p.l} style={{background:C.amberL,borderRadius:12,padding:"10px 8px",textAlign:"center"}}>
                  <div style={{fontSize:9,fontWeight:700,color:C.amber,letterSpacing:0.8,textTransform:"uppercase",marginBottom:3,display:"flex",alignItems:"center",justifyContent:"center",gap:3}}><Trophy width={9} height={9}/> {p.l}</div>
                  <div style={{fontWeight:700,fontSize:15,...St.mono,color:C.text}}>{p.v}</div>
                </div>
              ))}
            </div>

            {/* Range selector */}
            <div style={{display:"flex",background:C.fill,borderRadius:9,padding:2,marginBottom:12,gap:0}}>
              {RANGES.map(r=>{
                const sel=range===r.id;
                return (
                  <button key={r.id} onClick={()=>setRange(r.id)}
                    style={{flex:1,padding:"6px 0",border:"none",borderRadius:7,
                      background:sel?"#fff":"transparent",color:sel?C.text:C.subtle,
                      fontSize:12,fontWeight:sel?600:400,cursor:"pointer",fontFamily:FONT_BODY,
                      boxShadow:sel?"0 1px 3px rgba(0,0,0,0.12)":"none",transition:"all 0.15s"}}>
                    {r.label}
                  </button>
                );
              })}
            </div>

            {/* Charts */}
            {cd.length<2
              ?<div style={{...St.card,textAlign:"center",padding:"20px",color:C.subtle,fontSize:13,marginBottom:12}}>Troppo pochi dati per il periodo selezionato.</div>
              :<ExerciseCharts cd={cd} tRM={tRM} tPeso={tPeso} tVol={tVol} tReps={tReps} tRpe={tRpe}/>
            }

            {/* Proiezione carico */}
            {projection&&<LoadProjectionChart projection={projection}/>}

            {/* Prossima sessione — sotto i grafici */}
            {nextSugg&&(
              <div style={{...St.card,background:`${nextSugg.color}10`,marginBottom:12}}>
                <div style={{fontSize:10,fontWeight:700,color:nextSugg.color,letterSpacing:1,textTransform:"uppercase",marginBottom:6,display:"flex",alignItems:"center",gap:4}}><Leaderboard width={10} height={10}/> Prossima sessione</div>
                <div style={{fontWeight:700,fontSize:14,color:C.text,marginBottom:4}}>{nextSugg.title}</div>
                <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>{nextSugg.body}</div>
              </div>
            )}

            {/* Storico sessioni — unica card */}
            <div style={{...St.secTit,marginTop:4,marginBottom:10}}>Storico sessioni</div>
            <div style={{...St.card,padding:0,overflow:"hidden"}}>
              {occReversed.map((o,i)=>{
                const rm=calcAvg1RM(o.ex.sets),rpe=calcAvgRPE(o.ex.sets);
                const z=rpe?rpeZone(rpe):null;
                const isOpen=openSess===i;
                const validSets=filterWarmupSets(o.ex.sets).filter(s=>parseFloat(s.weight)>0||Number(s.reps)>0);
                const prevO=occReversed[i+1];
                const insight=progressionComment(o.ex.sets,o.ex.type||"compound",prevO?.ex.sets||null,_globalSettings.repRange,dbEx?.equipment);
                return(
                  <div key={i}>
                    <div onClick={()=>setOpenSess(isOpen?null:i)}
                      style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",cursor:"pointer"}}>
                      <div>
                        <div style={{fontSize:12,color:C.subtle,marginBottom:3}}>
                          {new Date(o.date).toLocaleDateString("it-IT",{weekday:"short",day:"numeric",month:"short"})}
                          {o.workoutName&&<><span style={{margin:"0 5px",color:C.border}}>·</span><span style={{color:C.muted}}>{o.workoutName}</span></>}
                        </div>
                        <div style={{fontWeight:700,fontSize:15,color:C.text,fontFamily:FONT_MONO}}>
                          {rm.toFixed(1)}<span style={{fontSize:12,color:C.subtle,fontWeight:400}}> kg 1RM</span>
                          <span style={{marginLeft:10,fontWeight:500,color:C.muted,fontSize:13}}>{validSets.length} serie</span>
                        </div>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        {insight&&<span style={{width:8,height:8,borderRadius:4,background:insight.color,display:"inline-block",flexShrink:0}}/>}
                        {showIntensita&&z&&<span style={{...St.tag(z.color,z.bg),fontSize:10}}>RPE {rpe.toFixed(1)}</span>}
                        <span style={{color:C.subtle,fontSize:14,marginLeft:4}}>{isOpen?"⌃":"⌄"}</span>
                      </div>
                    </div>
                    {isOpen&&(<>
                      <div style={{height:"0.5px",background:C.border}}/>
                      <div style={{padding:"12px 16px"}}>
                        {/* Header colonne */}
                        <div style={{display:"grid",gridTemplateColumns:cols,gap:8,marginBottom:8}}>
                          <div/>
                          {headers.map(h=><div key={h} style={{fontSize:10,fontWeight:600,color:C.subtle,textAlign:"center",letterSpacing:0.4}}>{h}</div>)}
                        </div>
                        {/* Righe serie */}
                        {validSets.map((s,j)=>{
                          const sRpe=s.rpe?rpeZone(Number(s.rpe)):null;
                          return(
                            <div key={j} style={{display:"grid",gridTemplateColumns:cols,gap:8,marginBottom:8,alignItems:"center"}}>
                              <div style={{fontSize:11,color:C.subtle,textAlign:"center",fontFamily:FONT_BODY}}>{j+1}</div>
                              <div style={cellStyle}>{s.weight||"—"}</div>
                              <div style={cellStyle}>{s.reps||"—"}</div>
                              {showIntensita&&<div style={{...cellStyle,color:sRpe?sRpe.color:C.subtle}}>{s.rpe||"—"}</div>}
                              {showIntensita&&<div style={{...cellStyle,color:C.muted}}>{s.rpe?String(rpe2rir(s.rpe).toFixed(1)):"—"}</div>}
                            </div>
                          );
                        })}
                        {insight&&_globalSettings.showInsights&&(
                          <div style={{marginTop:4,marginBottom:4}}>
                            <CommentCard c={insight}/>
                          </div>
                        )}
                      </div>
                    </>)}
                    {i<occReversed.length-1&&<div style={{height:"0.5px",background:"rgba(60,60,67,0.12)",margin:"0 16px"}}/>}
                  </div>
                );
              })}
            </div>
          </>)}
        <div style={{height:24}}/>
      </div>
    </div>
  );
}
